"use client";

/* -----------------------------------------------------------------------------
 * Imports
 * ----------------------------------------------------------------------------*/

import type * as React from "react";
import { Drawer as Vaul } from "vaul";
import { cn } from "../lib/utils";

/* -----------------------------------------------------------------------------
 *  Extend Drawer
 * ----------------------------------------------------------------------------*/

const DrawerRoot = Vaul.Root;
const DrawerTrigger = Vaul.Trigger;
const DrawerPortal = Vaul.Portal;
const DrawerOverlay = Vaul.Overlay;
const DrawerTitle = Vaul.Title;
const DrawerDescription = Vaul.Description;
const DrawerNested = Vaul.NestedRoot;

/* ----------------------------------------------------------------------------
 * Drawer - Content
 * ---------------------------------------------------------------------------*/

type DrawerContentElement = React.ElementRef<typeof Vaul.Content>;
type DrawerContentProps = React.ComponentPropsWithoutRef<typeof Vaul.Content>;

function DrawerContent(props: DrawerContentProps & { ref?: React.Ref<DrawerContentElement> }) {
  const { className, children, ref, ...contentProps } = props;

  return (
    <DrawerPortal>
      <DrawerOverlay className="fixed inset-0 bg-black/30 backdrop-blur-[2px] dark:bg-black/60" />
      <Vaul.Content
        {...contentProps}
        ref={ref}
        className={cn(
          "bg-raised flex flex-col fixed bottom-0 left-0 right-0 max-h-[82vh] rounded-t-2xl shadow-floating before:mx-auto before:mt-2.5 before:mb-1 before:h-1 before:w-9 before:shrink-0 before:rounded-full before:bg-grayA-5",
          className,
        )}
      >
        {children}
      </Vaul.Content>
    </DrawerPortal>
  );
}

/* ----------------------------------------------------------------------------
 * Exports
 * ---------------------------------------------------------------------------*/

export const Drawer = Object.assign({
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Content: DrawerContent,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Nested: DrawerNested,
});
